import { useEffect, useState } from 'react';

import { COTTY_BANNER, DECODE_GLYPHS } from '../lib/cottyBrand';

/** Shared cotty.sh "ctOS" visual FX — the decode banner + HUD backdrop —
 *  reused by the SSH connecting overlay and the login screen. */

export const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * The cotty.sh "decode" banner: the COTTY ANSI-shadow logo resolves out of
 * scrambling glyph-noise (Watch_Dogs decrypt vibe) on mount, then re-scrambles
 * on an ambient loop. Three stacked layers (amber base + red/cyan offsets with
 * `mix-blend-mode: screen`) give the chromatic-glitch flicker.
 */
function useDecodeBanner(): string {
    const [display, setDisplay] = useState(COTTY_BANNER);

    useEffect(() => {
        if (prefersReducedMotion()) {
            setDisplay(COTTY_BANNER);
            return;
        }

        const chars = COTTY_BANNER.split('');
        const isFixed = (c: string) => c === ' ' || c === '\n';
        let raf = 0;

        const runOnce = () => {
            const revealAt = chars.map((c) =>
                isFixed(c) ? 0 : 240 + Math.random() * 1150,
            );
            const start = performance.now();
            const frame = (now: number) => {
                const e = now - start;
                let out = '';
                for (let i = 0; i < chars.length; i++) {
                    const c = chars[i] ?? '';
                    if (isFixed(c)) {
                        out += c;
                    } else if (e >= (revealAt[i] ?? 0)) {
                        out += c;
                    } else {
                        out +=
                            DECODE_GLYPHS[
                                (Math.random() * DECODE_GLYPHS.length) | 0
                            ] ?? c;
                    }
                }
                setDisplay(out);
                if (e < 1600) {
                    raf = requestAnimationFrame(frame);
                } else {
                    setDisplay(COTTY_BANNER);
                }
            };
            raf = requestAnimationFrame(frame);
        };

        runOnce();
        // Re-scramble ambiently while the screen lingers.
        const loop = window.setInterval(runOnce, 5200);
        return () => {
            cancelAnimationFrame(raf);
            clearInterval(loop);
        };
    }, []);

    return display;
}

export function CottyBanner() {
    const display = useDecodeBanner();
    return (
        <div className="cotty-crt relative select-none">
            <pre className="cotty-ascii cotty-ascii-base cotty-ascii-glitch">{display}</pre>
            <pre className="cotty-ascii cotty-ascii-r" aria-hidden>
                {display}
            </pre>
            <pre className="cotty-ascii cotty-ascii-c" aria-hidden>
                {display}
            </pre>
        </div>
    );
}

/** Subtle ctOS backdrop: a drifting schematic grid, an optional center vignette,
 *  and faint CRT scanlines. No noise, no fake telemetry. */
export function CtosBackdrop({ vignette = true }: { vignette?: boolean }) {
    return (
        <>
            <div className="cotty-grid pointer-events-none absolute inset-0" aria-hidden />
            {vignette && (
                <div className="cotty-vignette pointer-events-none absolute inset-0" aria-hidden />
            )}
            <div className="cotty-scan pointer-events-none absolute inset-0" aria-hidden />
        </>
    );
}

/** HUD targeting frame (corner ticks) + an optional scanning sweep. Positions
 *  itself against the nearest `relative` ancestor. */
export function HudFrame({ sweep = true }: { sweep?: boolean }) {
    return (
        <div className="cotty-hud" aria-hidden>
            <i className="tl" />
            <i className="tr" />
            <i className="bl" />
            <i className="br" />
            {sweep && !prefersReducedMotion() && <div className="cotty-sweep" />}
        </div>
    );
}
